import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppLayout } from "./components/layout/AppLayout";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { RoleRedirect } from "./components/auth/RoleRedirect";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageViewTracker } from "./components/PageViewTracker";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Vessels from "./pages/Vessels";
import CalendarPage from "./pages/Calendar";
import Contacts from "./pages/Contacts";
import Assets from "./pages/Assets";
import Parts from "./pages/Parts";
import PurchaseOrders from "./pages/PurchaseOrders";
import MaintenanceWO from "./pages/Maintenance";
import PmPrograms from "./pages/PmPrograms";
import Documents from "./pages/Documents";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Docs from "./pages/Docs";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Apps from "./pages/Apps";
import CaseLabels from "./pages/CaseLabels";
import Customers from "./pages/Customers";
import Locations from "./pages/Locations";
import Bottling from "./pages/Bottling";
import BottlingClient from "./pages/BottlingClient";
import ProductionPlanner from "./pages/ProductionPlanner";
import { BottlingLayout } from "./components/bottling/BottlingLayout";
import { ClientBottlingLayout } from "./components/bottling/ClientBottlingLayout";
import { PortalLayout } from "./components/layout/PortalLayout";
import CustomerPortal from "./pages/CustomerPortal";
import ClientTankSchedule from "./pages/portal/ClientTankSchedule";
import CustomerProfile from "./pages/CustomerProfile";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <PageViewTracker />
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    <Route path="/" element={<RoleRedirect><Dashboard /></RoleRedirect>} />
                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/vessels" element={<Vessels />} />
                    <Route path="/contacts" element={<Contacts />} />
                    <Route path="/assets" element={<Assets />} />
                    <Route path="/parts" element={<Parts />} />
                    <Route path="/locations" element={<Locations />} />
                    <Route path="/purchase-orders" element={<PurchaseOrders />} />
                    <Route path="/maintenance" element={<MaintenanceWO />} />
                    <Route path="/pm-programs" element={<PmPrograms />} />
                    <Route path="/documents" element={<Documents />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/docs" element={<Docs />} />
                    <Route path="/customers" element={<Customers />} />
                    <Route path="/apps" element={<Apps />} />
                    <Route path="/apps/case-labels" element={<CaseLabels />} />
                  </Route>
                  {/* Admin bottling app — own layout */}
                  <Route element={<ProtectedRoute><BottlingLayout /></ProtectedRoute>}>
                    <Route path="/apps/bottling" element={<Bottling />} />
                    <Route path="/apps/bottling/planner" element={<ProductionPlanner />} />
                  </Route>
                  {/* Customer portal — simplified layout for client_viewer users */}
                  <Route element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
                    <Route path="/portal" element={<CustomerPortal />} />
                    <Route path="/portal/profile" element={<CustomerProfile />} />
                    <Route path="/portal/bottling" element={<BottlingClient />} />
                    <Route path="/portal/tanks" element={<ClientTankSchedule />} />
                  </Route>
                  {/* Legacy client bottling route — redirect handled by PortalLayout */}
                  <Route element={<ProtectedRoute><ClientBottlingLayout /></ProtectedRoute>}>
                    <Route path="/client/bottling" element={<BottlingClient />} />
                  </Route>
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
